import * as yup from "yup";

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Slide,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  useAddNewCourseMutation,
  useDeleteCvCourseMutation,
  useEditCvCourseMutation
} from "../../../lib/cvsSlice";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import SaveIcon from "@mui/icons-material/Save";
import UploadCourseDoc from "components/UploadCourseDoc";
import { cyan } from "@mui/material/colors";
import { red } from "@mui/material/colors";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";

const courseSchema = yup.object().shape({
  name: yup.string().required("Course name is required"),
  monthFinished: yup.string().required("Month completed course is required"),
  yearFinished: yup.string().required("Year completed course is required"),
  description: yup.string().required("Course description is required"),
  excludeInCv: yup.boolean(),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Courses = ({ courses, cvId }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const [cvCourses, setCvCourses] = useState(courses);
  const [courseId, setCourseId] = useState();
  const [localExcludeInCv, setLocalExcludeInCv] = useState({});

  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(courseSchema),
    defaultValues: {
      name: "",
      monthFinished: "",
      yearFinished: "",
      description: "",
      excludeInCv: false,
    },
  });

  const [addNewCourse] = useAddNewCourseMutation();

  const submitResults = async (e) => {
    const newCourse = e
    await addNewCourse({ cvId, coursee: [newCourse] }).unwrap();

    courses.push(newCourse);
    handleClose();
    toast.success("Course added successfully");
  };

  const [deleteCvCourse] = useDeleteCvCourseMutation();

  const handleRemove = async (crs) => {
    await deleteCvCourse({ cvId, courseeId: crs }).unwrap();
    const updatedCvCourses = courses.filter((course) => course.id !== crs);
    setCvCourses(updatedCvCourses);
    courses.splice(courses.findIndex((course) => course._id === crs), 1);
    setCvCourses([...courses]);
    toast.info("Course removed successfully");
  };

  const [editCvCourse] = useEditCvCourseMutation();
  const handleEdit = (courseId, mode) => {
    setOpen(true);
    setMode(mode);
    setCourseId(courseId);
  };

  useFormPersist("courses", {
    watch,
    setValue,
    storage: window.localStorage, // default window.sessionStorage
  });

  useEffect(() => {
    const initialLocalExcludeInCv = {};
    courses.forEach((course) => {
      const savedState = localStorage.getItem(`excludeInCv-${course._id}`);
      initialLocalExcludeInCv[course._id] = savedState === 'true';
      console.log(`Initial state for ${course._id}: ${initialLocalExcludeInCv[course._id]}`);
    });
  
    setLocalExcludeInCv(initialLocalExcludeInCv);
  }, [courses]);

  useEffect(() => {
    const data = localStorage.getItem("courses");
    if (data !== null) JSON.parse(data);
  }, []);

  useEffect(() => {
    localStorage.setItem("courses", JSON.stringify(courses));
  }, [courses]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = [];
  for (let year = 2023; year >= 1940; year--) {
    years.push(year);
  }

  const handleInputChange = (event, courseId, fieldName) => {
    const courseElement = courses.find((course) => course._id === courseId);
    courseElement[fieldName] = event.target.value;
  
    const updatedCourse = courses.map((course) => {
      if (course._id === courseId) {
        return { ...course, [fieldName]: event.target.value };
      }
      return course;
    });
    setCvCourses(updatedCourse);
  };

  const handleToggleChange = async (newExcludeInCv, courseId, fieldName, updatedValue) => {
    try {
      const updatedCourses = courses.map((course) => {
        if (course._id === courseId) {
          return {
            ...course,
            [fieldName]: updatedValue,
          };
        }
        return course;
      });

      // Update the local state
      setLocalExcludeInCv((prevLocalExcludeInCv) => ({
        ...prevLocalExcludeInCv,
        [courseId]: newExcludeInCv,
      }));

      // Update localStorage to persist the toggle value across refreshes
      localStorage.setItem(`excludeInCv-${courseId}`, newExcludeInCv.toString());

      // Update the certificate on the backend
      await editCvCourse({
        cvId: cvId,
        courseId: courseId,
        coursee: updatedCourses.map((course) => ({
          ...course,
          excludeInCv: newExcludeInCv,
        })),
      });

      // Update the certs array in the state
      setCvCourses(updatedCourses);
    } catch (error) {
      console.error('Error updating toggle:', error);
    }
  };

    
  //Edit mode functionality
  const coursesElement = courses.find((course) => course._id === courseId);
  const renderSingleElement = () => {
  return (
  <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={{ mt: 1.25 }}
    >
      <Grid item xs={12}>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Course Name"
              fullWidth
              autoFocus
              error={!!errors.name}
              helperText={errors?.name ? errors.name?.message : ""}
              value={coursesElement.name}
              onChange={(e) => handleInputChange(e, courseId, "name")}

            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="yearFinished"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Year completed"
              fullWidth
              error={!!errors.yearFinished}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "center",
                  },
                },
              }}
              helperText={
                errors?.yearFinished
                  ? errors.yearFinished?.message
                  : ""
              }
              value={coursesElement.yearFinished}
              onChange={(e) => handleInputChange(e, courseId, "yearFinished")}
            >

            {years.map((year) => (
              <MenuItem value={year} key={year}>
                {year}
              </MenuItem>
            ))}

            </TextField>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="monthFinished"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Month finished"
              fullWidth
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "center",
                  },
                },
              }}
              error={!!errors.monthFinished}
              helperText={
                errors?.monthFinished
                  ? errors.monthFinished?.message
                  : ""
              }
              value={coursesElement.monthFinished}
              onChange={(e) => handleInputChange(e, courseId, "monthFinished")}
            >
              {monthNames.map((month) => (
                <MenuItem value={month} key={month}>
                  {month}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="description"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Description"
              multiline
              maxRows={6}
              fullWidth
              error={!!errors.description}
              helperText={
                errors?.description ? errors.description?.message : ""
              }
              value={coursesElement.description}
              onChange={(e) => handleInputChange(e, courseId, "description")}
            />
          )}
        />
      </Grid>
    </Grid>
 );
};


const submitEditResults = async () => { 
  await editCvCourse({
    cvId,
    courseId,
    coursee: [coursesElement],
  }).unwrap();
  handleClose();
  toast.info("Education updated successfully");
};


  return (
    <div id="courses">
      <ToastContainer autoClose={1500} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ mt: 2.5, p: 1.875 }}
          >
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: "5px" }}

            >
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  // justifyContent="center"
                  // alignItems="center"
                >
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 2, pb: 0 }}
                    >
                      Courses &nbsp;
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="add"
                      onClick={() => handleOpen("add")}
                    >
                      <AddIcon color="primary" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpen("edit")}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {courses?.map((course) => (
                  <List key={course._id} sx={{ p: 0 }}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: cyan[700] }}>
                          <LocalLibraryOutlinedIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ pl: "10px" }}
                        primary={
                          <Typography variant="subtitle2" component="div">
                            Course name: {course.name} 
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" display="block">
                            Date: {course.monthFinished} {course.yearFinished}
                            <Typography variant="caption" display="block">
                            Course Description: {course.description}
                           </Typography>
                           <Typography variant="caption" display="block">
                            Exclude in cv: {course.excludeInCv ? "Yes" : "No"}
                          </Typography>
                          </Typography>
                        }
                      />
                    <Grid item xs={12} sm={3} sx={{ textAlign: 'right', pr: '40px' }}>
                    <FormControl>
                    <FormHelperText error={!!errors.excludeInCv}>
                      {errors?.excludeInCv ? errors.excludeInCv?.message : ''}
                    </FormHelperText>
                    <Controller
                    key = {course._id}
                    name={`excludeInCv-${course._id}`}
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Tooltip title="This will exclude the section from the CV">
                        <FormControlLabel
                          sx={{ m: 0 }}

                          control={
                            <Switch
                            onChange={(e) => {
                              const newExcludeInCv = e.target.checked;
                            
                              // Update the local state for the specific certificate immediately
                                setLocalExcludeInCv((prevLocalExcludeInCv) => ({
                                  ...prevLocalExcludeInCv,
                                  [course._id]: newExcludeInCv,
                                }));
                              
                                handleToggleChange(newExcludeInCv, course._id, 'excludeInCv');
                              
                                console.log('Toggle updated successfully');
                              }}
                              checked={localExcludeInCv[course._id] || false}
                              {...field}
                              ref={null}
                            />

                          }
                        />
                      </Tooltip>
                    )}
                   />
                  </FormControl>
                  </Grid>

                    </ListItem>
                  </List>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        {mode === "add" && (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>
            <DialogTitle>Add a New Course</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1.25 }}

              >
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Course Name"
                        fullWidth
                        autoFocus
                        error={!!errors.name}
                        helperText={errors?.name ? errors.name?.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="yearFinished"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Year completed"
                        fullWidth
                        error={!!errors.yearFinished}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        helperText={
                          errors?.yearFinished
                            ? errors.yearFinished?.message
                            : ""
                        }
                      >
                      {years.map((year) => (
                        <MenuItem value={year} key={year}>
                          {year}
                        </MenuItem>
                      ))}

                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="monthFinished"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Month finished"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.monthFinished}
                        helperText={
                          errors?.monthFinished
                            ? errors.monthFinished?.message
                            : ""
                        }
                      >
                        {monthNames.map((month) => (
                          <MenuItem value={month} key={month}>
                            {month}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        multiline
                        maxRows={6}
                        fullWidth
                        error={!!errors.description}
                        helperText={
                          errors?.description ? errors.description?.message : ""
                        }
                      />
                    )}
                  />
              </Grid>
             <Grid item xs={12}>
              <UploadCourseDoc />
             </Grid>
            </Grid>

            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1.25 }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove Course</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: "5px" }}

              >
                <Grid item xs={12}>
                  {courses?.map((course) => (
                    <List key={course._id} sx={{ p: 0 }}>
                      <ListItem
                        sx={{ pl: "10px" }}
                        secondaryAction={
                          <Stack alignItems="right" direction="row">
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleEdit(course._id, "editnew")}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(course._id)}
                            >
                              <DeleteIcon sx={{ color: red[400] }}
 />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          sx={{ pr: "20px", m: 0 }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              Course name: {course.name} 
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" display="block">
                              Date: {course.monthFinished} {course.yearFinished}
                              <Typography variant="caption" display="block">
                              Course Description: {course.description}
                             </Typography>
                             <Typography variant="caption" display="block">
                                Exclude in cv: {course.excludeInCv ? "Yes" : "No"}
                             </Typography>
                            </Typography>
                            
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1.25 }}

                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={() => {
              submitEditResults();
            }}
          >
            <DialogTitle>Edit Courses</DialogTitle>
            <DialogContent dividers={true}>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1.25 }}

                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}


      </Dialog>
    </div>
  );
};

export default Courses;
